"use client";
import { HasAdContext } from "components/framework/AdContextWrapper";
import { ICommonAppData } from "lib/types/sanity";
import { useContext, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import styled, { css } from "styled-components";



//#region [Styles]
type SAdContainerProps = {
	$hasAd?: boolean;
};

const SAdContainer = styled.div<SAdContainerProps>`
	background-color: rgba(0, 0, 0, 0.7);
	padding: 0 0 0.9rem 0;
	overflow: hidden;
	height: calc(62.6vw + 1.8rem);
	max-height: calc(62.6vw + 1.8rem);
	// Order 0 when the ad is at the top of the page
	order: 2;
	transition: height 0.2s, padding 0.2s;

	${props => {
		if (!props.$hasAd) {
			return css`
				height: 0 !important;
				padding: 0 !important;
			`;
		}
		return null;
	}}

	@media screen and (min-width: 641px) {
		// Order 2 when the ad is futher down the page.
		// All elements that should appeat above the ad must have order 1. Everything below the ad must have order 3 or higher
		order: 0;
		//height: calc(222px + 1.8rem) !important;
		//max-height: calc(222px + 1.8rem) !important;
		max-height: calc(${(300 / 2000) * 100}vw + 1.8rem)  !important;
		background-color: #1b1b1b;

		${props => {
		if (!props.$hasAd) {
			return css`
						height: 0 !important;
						padding: 0 !important;
					`;
		}
		return css`
					height: calc(${(300 / 2000) * 100}vw + 1.8rem)  !important;
				`;
	}}
	}

	@media screen and (min-width: 1480px) {
		max-height: calc(222px + 1.8rem) !important;

		${props => {
		if (!props.$hasAd) {
			return css`
					height: 0 !important;
					padding: 0 !important;
				`;
		}
		return css`
				height: calc(222px + 1.8rem) !important;
			`;
	}}
	}

	&::before {
		content: "Annonse";
		font-size: 0.6rem;
		line-height: 0.9rem;
		text-align: center;
		color: white;
		display: block;
	}


`;

const SAdElement = styled.div`
	margin: 0 auto;
	padding: 0 !important;
	height: calc(100% - 0.9rem) !important;
	max-width: 1480px;

`;

const SEmptyAdPlaceholder = styled.div`
	padding: 0;
	margin: 0;
	height: 100%;
`;
//#endregion

//#region [Props]
type AdnuntiusAdProps = {
	appData: ICommonAppData,
	index: number,
	className?: string;
	hideOnMobile: boolean;
	hideOnDesktop?: boolean;
	debugLabel?: string;
	showAdAfterFirstScroll?: boolean;
};
//#endregion

//#region [Component]
export default function AdnuntiusAd({ appData, index, className, hideOnMobile, hideOnDesktop = false, debugLabel, showAdAfterFirstScroll = false }: AdnuntiusAdProps) {

	const [isLarge, setIsLarge] = useState<boolean | null>(null);
	const [isVisible, setIsVisible] = useState(!showAdAfterFirstScroll);
	const hasAdContext = useContext(HasAdContext);
	const [ref, isInViewport] = useInView({ threshold: 0.5 });

	useEffect(() => {
		// in case the useState initializer for isLarge fires before window is available
		setIsLarge(testIsLarge());
	}, [])

	useEffect(() => {
		//console.debug("showAdAfterFirstScroll", showAdAfterFirstScroll, "hideOnMobile", hideOnMobile, "isLarge", isLarge);
		if (showAdAfterFirstScroll === true && hideOnMobile === false && isLarge === false) {
			const onFirstScroll = () => {
				setIsVisible(true);
				window.removeEventListener("scroll", onFirstScroll);

			};

			window?.addEventListener("scroll", onFirstScroll);

			return () => window?.removeEventListener("scroll", onFirstScroll);
		}
	}, [showAdAfterFirstScroll, isLarge, hideOnMobile]);

	if ((appData?.settings?.adIds?.length ?? 0) <= index) {
		console.debug(`${debugLabel ? `[${debugLabel}] ` : ""}No ad for index ${index}`, appData?.settings?.adIds);
		return null;
	}

	// use explicit === false and === true to prevent null from doing anything
	if (isLarge === null || (hideOnMobile && isLarge === false) || (hideOnDesktop && isLarge === true)) {
		return null;
	}

	return <SAdContainer className={className} $hasAd={hasAdContext[index]} data-debuglabel={debugLabel}>
		<SEmptyAdPlaceholder ref={ref}>
			{(isVisible || isLarge || isInViewport) && <SAdElement id={appData.settings!.adIds![index].adPlacementName}></SAdElement>}
		</SEmptyAdPlaceholder>
	</SAdContainer>;
}
//#endregion

function testIsLarge(): boolean | null {
	if (typeof window !== "undefined" && window?.["matchMedia"]) {
		const mql = window.matchMedia("(min-width: 641px)")
		return mql.matches;
	}
	return null;
}